import { useCallback, useMemo } from "react";
import { getIn, useFormikContext } from "formik";
import {
  useBalanceTypeFamilies,
  useBalanceTypes,
  // @ts-expect-error pure js
} from "../../hooks/use-balances";
import {
  getBalanceTypeFamilyOptions,
  getBalanceTypeOptions,
} from "../../utils/balances";
import BalanceTypeCascade from "./BalanceTypeCascade";
import type {
  BalanceTypeCatalog,
  BalanceTypeFamily,
} from "../../models/balance";

interface BalanceTypeSelectProps {
  prefix?: string;
  isEdit?: boolean;
  disabled?: boolean;
  excludeCategories?: string[];
}

/**
 * Choix du type hors contexte d'affectation (édition groupée) : le domaine de la ligne
 * suffit à filtrer, sans régime fiscal connu.
 */
export default function BalanceTypeSelect({
  prefix = "",
  isEdit = false,
  disabled,
  excludeCategories = [],
}: BalanceTypeSelectProps) {
  const { balanceTypes, isLoading } = useBalanceTypes() as {
    balanceTypes: BalanceTypeCatalog | undefined;
    isLoading: boolean;
  };
  const { families } = useBalanceTypeFamilies() as {
    families: BalanceTypeFamily[];
  };
  const { values } = useFormikContext<Record<string, unknown>>();
  const domaineBalance = getIn(values, `${prefix}domaine`) as string;

  const familyOptions = useMemo(
    () =>
      getBalanceTypeFamilyOptions(
        getBalanceTypeOptions(
          domaineBalance,
          { balanceTypes },
          { isEdit, excludeCategories },
        ),
        families,
      ),
    [domaineBalance, balanceTypes, isEdit, excludeCategories, families],
  );

  const typeOptionsForFamily = useCallback(
    (family: string) =>
      getBalanceTypeOptions(
        domaineBalance,
        { balanceTypes },
        { isEdit, excludeCategories, family },
      ),
    [domaineBalance, balanceTypes, isEdit, excludeCategories],
  );

  if (isLoading) return null;

  return (
    <BalanceTypeCascade
      prefix={prefix}
      familyOptions={familyOptions}
      typeOptionsForFamily={typeOptionsForFamily}
      disabled={disabled}
    />
  );
}
